import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, CheckCircle2, AlertCircle, ExternalLink, Copy, Check, QrCode, ShieldCheck, Lock, Zap, Maximize2 } from 'lucide-react';
import { getSavedPaymentGatewayConfigs, PaymentGatewayConfig } from './PaymentMethodsModal';
import { EmbeddedPaymentIframeModal } from './EmbeddedPaymentIframeModal';
import { safeWindowOpen } from '../lib/mobilePaymentUtils';
import { safeFetchJson } from '../lib/safeFetchJson';

interface MercadoPagoSmartButtonsProps {
  amountBrl: number;
  orderId?: string;
  items?: Array<{ name: string; price: number; quantity: number }>;
  mode?: 'production' | 'test';
  payerEmail?: string;
  payerName?: string;
  customDescription?: string;
  onSuccess?: (details: { transactionId: string; orderId?: string; rawDetails?: any }) => void;
  onError?: (errorMessage: string) => void;
  onCancel?: () => void;
}

declare global {
  interface Window {
    MercadoPago: any;
  }
}

export const MercadoPagoSmartButtons: React.FC<MercadoPagoSmartButtonsProps> = ({
  amountBrl,
  orderId,
  items,
  mode,
  payerEmail,
  payerName,
  customDescription,
  onSuccess,
  onError,
  onCancel
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const brickInstanceRef = useRef<any>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [resolvedMode, setResolvedMode] = useState<'production' | 'test'>('production');
  const [preferenceId, setPreferenceId] = useState<string | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [isIframeModalOpen, setIsIframeModalOpen] = useState<boolean>(false);
  const [paymentCompleted, setPaymentCompleted] = useState<boolean>(false);
  
  // PIX state
  const [showPix, setShowPix] = useState<boolean>(false);
  const [pixLoading, setPixLoading] = useState<boolean>(false);
  const [pixData, setPixData] = useState<{ qrCode?: string; qrCodeBase64?: string; paymentId?: string } | null>(null);
  const [copiedPix, setCopiedPix] = useState<boolean>(false);
  
  // Manual confirmation state
  const [manualTxId, setManualTxId] = useState('');
  const [isConfirmingManual, setIsConfirmingManual] = useState(false);

  const configs = getSavedPaymentGatewayConfigs();
  const mpConfig: Partial<PaymentGatewayConfig> = configs.MercadoPago || {};

  // 1. Fetch configuration & Public Key
  useEffect(() => {
    let isMounted = true;
    const isTestMode = mode === 'test' || mpConfig.useTestMode === true;
    const activeMode = isTestMode ? 'test' : 'production';
    setResolvedMode(activeMode);

    const localKey = isTestMode
      ? (mpConfig.testPublicKey || mpConfig.publicKey)
      : (mpConfig.productionPublicKey || mpConfig.publicKey);

    if (localKey && localKey.length > 20 && !localKey.includes('1234-5678')) {
      setPublicKey(localKey);
      setLoading(false);
      return;
    }

    fetch(`/api/mercadopago/public-key?mode=${activeMode}`)
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted) return;
        if (data.success && data.publicKey && !data.publicKey.includes('1234-5678')) {
          setPublicKey(data.publicKey);
        } else {
          setPublicKey(null);
        }
      })
      .catch((err) => {
        console.warn('Could not fetch Mercado Pago public key from backend:', err);
        if (isMounted) setPublicKey(null);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [mode, mpConfig.useTestMode, mpConfig.publicKey, mpConfig.productionPublicKey, mpConfig.testPublicKey]);

  // 2. Load SDK & Generate Order / Preference & Render Wallet Brick
  useEffect(() => {
    if (paymentCompleted) return;

    let isSubscribed = true;
    const activeOrderId = orderId || `order-${Date.now()}`;

    const loadSdkAndRenderBrick = async () => {
      try {
        setLoading(true);
        setSdkError(null);

        // 2a. Request backend order creation (preference) safely without throwing on HTML
        const orderRes = await safeFetchJson<any>('/api/mercadopago/create-order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amountBrl,
            orderId: activeOrderId,
            mode: resolvedMode,
            title: customDescription || `Pagamento Do Japão #${activeOrderId}`,
            description: customDescription || `Pedido #${activeOrderId}`,
            items: items || [
              {
                name: customDescription || `Pagamento Do Japão #${activeOrderId}`,
                price: amountBrl,
                quantity: 1
              }
            ],
            payerEmail: payerEmail || 'comprador@dojapao.co.jp',
            payerName: payerName || 'Cliente Do Japão',
            configOverride: mpConfig
          })
        });

        const orderData = orderRes.data || {};
        if (!isSubscribed) return;

        if (orderData.id || orderData.orderId || orderData.checkoutUrl) {
          const prefId = orderData.id || orderData.orderId || orderData.preferenceId;
          const url = orderData.checkoutUrl || orderData.init_point || orderData.sandbox_init_point;
          setPreferenceId(prefId);
          setCheckoutUrl(url);

          // 2b. If public key and window.MercadoPago or dynamic SDK script available, render official wallet brick
          if (publicKey && prefId && !prefId.startsWith('pref-simulated') && !prefId.startsWith('pref-mp-')) {
            // Load script if not present
            if (!window.MercadoPago) {
              const scriptId = 'mercadopago-sdk-script';
              if (!document.getElementById(scriptId)) {
                const script = document.createElement('script');
                script.id = scriptId;
                script.src = 'https://sdk.mercadopago.com/js/v2';
                script.async = true;
                document.head.appendChild(script);
                await new Promise<void>((resolve) => {
                  script.onload = () => resolve();
                  script.onerror = () => resolve();
                });
              }
            }

            if (window.MercadoPago && containerRef.current) {
              try {
                const mp = new window.MercadoPago(publicKey.trim(), { locale: 'pt-BR' });
                const bricksBuilder = mp.bricks();

                containerRef.current.innerHTML = '';
                const renderId = `mp_wallet_btn_${Date.now()}`;
                containerRef.current.id = renderId;

                if (brickInstanceRef.current?.unmount) {
                  try {
                    brickInstanceRef.current.unmount();
                  } catch (e) {}
                }

                const controller = await bricksBuilder.create('wallet', renderId, {
                  initialization: {
                    preferenceId: prefId,
                    redirectMode: 'modal'
                  },
                  customization: {
                    texts: {
                      valueProp: 'convenience'
                    }
                  },
                  callbacks: {
                    onReady: () => {
                      if (isSubscribed) setLoading(false);
                    },
                    onSubmit: () => {
                      console.log('MercadoPago Wallet Brick Submitted');
                    },
                    onError: (err: any) => {
                      console.warn('MercadoPago Brick Error / Notice:', err);
                      if (isSubscribed) setLoading(false);
                    }
                  }
                });

                brickInstanceRef.current = controller;
              } catch (bErr) {
                console.warn('MercadoPago Brick render notice:', bErr);
              }
            }
          }
        } else {
          throw new Error(orderData.error || 'Erro ao gerar ordem no Mercado Pago');
        }
      } catch (err: any) {
        console.warn('MercadoPago loading error:', err);
        setSdkError(err.message || 'Não foi possível carregar os botões automáticos do Mercado Pago.');
      } finally {
        if (isSubscribed) setLoading(false);
      }
    };

    loadSdkAndRenderBrick();

    return () => {
      isSubscribed = false;
      if (brickInstanceRef.current?.unmount) {
        try {
          brickInstanceRef.current.unmount();
        } catch (e) {}
      }
    };
  }, [publicKey, resolvedMode, amountBrl, orderId, paymentCompleted]);

  // Handle generating instant PIX with Mercado Pago
  const handleGeneratePix = async () => {
    try {
      setPixLoading(true);
      setShowPix(true);
      const res = await safeFetchJson<any>('/api/mercadopago/create-pix', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amountBrl,
          description: customDescription || `PIX Mercado Pago #${orderId || Date.now()}`,
          externalReference: orderId || `pix-${Date.now()}`,
          payerEmail: payerEmail || 'financeiro@dojapao.co.jp',
          payerName: payerName || 'Cliente Do Japão',
          configOverride: mpConfig
        })
      });

      const data = res.data;
      if (data && data.success) {
        setPixData({
          qrCode: data.qrCode,
          qrCodeBase64: data.qrCodeBase64,
          paymentId: data.paymentId
        });
      } else {
        throw new Error(data?.error || res.error || 'Erro ao gerar chave PIX');
      }
    } catch (err: any) {
      console.warn('Error generating Mercado Pago PIX:', err);
      setSdkError(err.message || 'Erro ao gerar PIX do Mercado Pago');
    } finally {
      setPixLoading(false);
    }
  };

  const handleManualConfirm = () => {
    setIsConfirmingManual(true);
    const txId = manualTxId.trim() || `MP-MANUAL-${Date.now()}`;
    setTimeout(() => {
      setPaymentCompleted(true);
      if (onSuccess) {
        onSuccess({
          transactionId: txId,
          orderId: preferenceId || orderId,
          rawDetails: { manual: true, confirmedAt: new Date().toISOString() }
        });
      }
      setIsConfirmingManual(false);
    }, 500);
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Header Badge */}
      <div className="p-3.5 bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200 rounded-2xl flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-[#009EE3] text-white flex items-center justify-center font-black text-sm shadow-xs shrink-0">
            <Zap className="w-4 h-4 fill-white" />
          </div>
          <div>
            <span className="text-xs font-black text-slate-800 block">Mercado Pago Checkout Oficial</span>
            <span className="text-[10px] text-slate-500 font-semibold block">PIX, Saldo Mercado Pago & Cartões Nacionais</span>
          </div>
        </div>
        <span
          className={`px-2.5 py-1 text-[10px] font-black rounded-full border ${
            resolvedMode === 'test'
              ? 'bg-amber-100 text-amber-800 border-amber-300'
              : 'bg-emerald-100 text-emerald-800 border-emerald-300'
          }`}
        >
          {resolvedMode === 'test' ? 'Sandbox (Teste)' : '● Modo Produção (Live)'}
        </span>
      </div>

      {/* Amount Display */}
      <div className="p-3 bg-white rounded-xl border border-slate-200 flex items-center justify-between">
        <span className="text-xs font-bold text-slate-600">Total a pagar via Mercado Pago:</span>
        <span className="text-sm font-black text-sky-800 font-mono">
          R$ {amountBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="py-6 text-center space-y-2 bg-slate-50 rounded-2xl border border-slate-200">
          <RefreshCw className="w-5 h-5 text-sky-600 animate-spin mx-auto" />
          <p className="text-xs font-bold text-slate-600">Gerando ordem e botões dinâmicos do Mercado Pago...</p>
        </div>
      )}

      {/* Error alert if any */}
      {sdkError && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <span className="font-bold block">Aviso do Checkout Mercado Pago:</span>
            <span>{sdkError}</span>
          </div>
        </div>
      )}

      {/* Official Wallet Brick Container */}
      <div ref={containerRef} className="space-y-2 min-h-[44px]" />

      {/* Action Buttons for Direct Checkout */}
      {checkoutUrl && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
          <button
            type="button"
            onClick={() => setIsIframeModalOpen(true)}
            className="w-full py-3 px-4 bg-gradient-to-r from-[#009EE3] to-[#007EB5] hover:from-[#0089C7] hover:to-[#006EA0] text-white font-extrabold text-xs rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <Maximize2 className="w-4 h-4" />
            <span>Pagar na Janela Segura</span>
          </button>

          <button
            type="button"
            onClick={() => safeWindowOpen(checkoutUrl)}
            className="w-full py-3 px-4 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <ExternalLink className="w-4 h-4 text-slate-500" />
            <span>Abrir Mercado Pago</span>
          </button>
        </div>
      )}

      {/* PIX Instantâneo Option */}
      <div className="pt-2 border-t border-slate-200 space-y-3">
        {!showPix ? (
          <button
            type="button"
            onClick={handleGeneratePix}
            disabled={pixLoading}
            className="w-full py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            {pixLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin text-emerald-600" />
            ) : (
              <QrCode className="w-4 h-4 text-emerald-600" />
            )}
            <span>Gerar QR Code PIX Mercado Pago</span>
          </button>
        ) : (
          <div className="p-4 bg-emerald-50/70 border border-emerald-300 rounded-2xl space-y-3">
            <div className="flex items-center justify-between text-xs font-black text-emerald-900 border-b border-emerald-200 pb-2">
              <span className="flex items-center gap-1.5">
                <QrCode className="w-4 h-4 text-emerald-600" />
                PIX Instantâneo Mercado Pago
              </span>
              <span className="text-[10px] bg-emerald-200/60 text-emerald-800 px-2 py-0.5 rounded-full font-bold">
                Aprovação Imediata
              </span>
            </div>

            {pixData?.qrCodeBase64 && (
              <div className="flex justify-center p-2 bg-white rounded-xl border border-emerald-200 max-w-[180px] mx-auto shadow-xs">
                <img src={pixData.qrCodeBase64} alt="QR Code PIX Mercado Pago" className="w-full h-auto rounded-lg" />
              </div>
            )}

            {pixData?.qrCode && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[11px] font-bold text-slate-600">
                  <span>Código Copia e Cola:</span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(pixData.qrCode!);
                      setCopiedPix(true);
                      setTimeout(() => setCopiedPix(false), 2000);
                    }}
                    className="text-emerald-700 hover:text-emerald-900 flex items-center gap-1 font-extrabold cursor-pointer"
                  >
                    {copiedPix ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                    <span>{copiedPix ? 'Copiado!' : 'Copiar Chave'}</span>
                  </button>
                </div>
                <div className="p-2 bg-white rounded-xl border border-emerald-200 font-mono text-[10px] text-slate-700 break-all select-all max-h-16 overflow-y-auto">
                  {pixData.qrCode}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Direct Confirmation / Transação ID */}
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
        <label className="text-[11px] font-bold text-slate-700 block">
          ID da Transação / Pedido Mercado Pago:
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={manualTxId}
            onChange={(e) => setManualTxId(e.target.value)}
            placeholder="Ex: MP-109283719 (Opcional)"
            className="flex-1 bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-sky-500"
          />
          <button
            type="button"
            disabled={isConfirmingManual}
            onClick={handleManualConfirm}
            className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl transition-colors flex items-center gap-1 shrink-0 cursor-pointer disabled:opacity-50"
          >
            {isConfirmingManual ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="w-3.5 h-3.5" />
            )}
            <span>Confirmar</span>
          </button>
        </div>
      </div>

      <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-semibold">
        <span className="flex items-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5 text-sky-600" />
          Compra Protegida Mercado Pago
        </span>
        <span className="flex items-center gap-1">
          <Lock className="w-3 h-3 text-slate-400" />
          Criptografia SSL 256-bit
        </span>
      </div>

      {/* Embedded Iframe Modal */}
      <EmbeddedPaymentIframeModal
        isOpen={isIframeModalOpen}
        onClose={() => setIsIframeModalOpen(false)}
        checkoutUrl={checkoutUrl}
        gatewayName="Mercado Pago"
        amountBrl={amountBrl}
        title={customDescription || `Pagamento Do Japão #${orderId}`}
        orderId={orderId || preferenceId || ''}
      />
    </div>
  );
};

export default MercadoPagoSmartButtons;
